import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  KeyValuePairs,
  Link,
  Modal,
  Select,
  SelectProps,
  SpaceBetween,
  Tabs,
  Table,
  TextFilter,
  StatusIndicator,
  StatusIndicatorProps,
} from '@cloudscape-design/components';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PropertyFilterQuery, PropertyFilterOption } from '@cloudscape-design/collection-hooks';
import { useMemo, useState } from 'react';

import {
  createChainReport,
  getChainAggregate,
  linkChainPlaces,
  listChainPlaces,
  listChainReports,
  listChainSearches,
  listChainLocationAudit,
  listChainSuggestedLinks,
  unlinkChainPlaces,
  updateChain,
  type ChainPlace,
  type ChainReport,
  type ChainSuggestedLink,
} from '../../../common/api/chains';
import { getPlaceMeta } from '../../../common/api/places';
import { BUSINESS_TYPES, Place } from '../../../common/types';
import { DashboardViewLayout } from '../../../components/DashboardViewLayout';
import { PlaceTable } from '../../../components/dashboard/PlaceTable';

const SEARCH_STATUS_INDICATOR: Record<string, StatusIndicatorProps.Type> = {
  pending: 'pending',
  discovering: 'in-progress',
  review: 'info',
  creating: 'in-progress',
  complete: 'success',
  failed: 'error',
  cancelled: 'stopped',
};

const AUDIT_ACTION_LABEL: Record<string, string> = {
  add_manual: 'Added (manual)',
  link_existing: 'Linked existing place',
  move_existing: 'Moved from another chain',
  link: 'Linked',
  move: 'Moved',
  unlink: 'Removed',
};

export default function ChainDetailPage() {
  const { chainUuid } = useParams({ from: '/dashboard/chains_/$chainUuid/' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState('locations');
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const aggQuery = useQuery({
    queryKey: ['chain-aggregate', chainUuid],
    queryFn: () => getChainAggregate(chainUuid),
  });

  if (aggQuery.isLoading) {
    return (
      <DashboardViewLayout
        breadcrumbs={[{ text: 'Chains', href: '/dashboard/chains' }]}
        header={<Header variant="h1">Loading…</Header>}
      >
        <Container />
      </DashboardViewLayout>
    );
  }

  if (aggQuery.error || !aggQuery.data) {
    return (
      <DashboardViewLayout
        breadcrumbs={[{ text: 'Chains', href: '/dashboard/chains' }]}
        header={<Header variant="h1">Chain</Header>}
      >
        <Alert type="error" header="Failed to load chain">
          {String(aggQuery.error)}
        </Alert>
      </DashboardViewLayout>
    );
  }

  const agg = aggQuery.data;

  return (
    <DashboardViewLayout
      breadcrumbs={[
        { text: 'Chains', href: '/dashboard/chains' },
        { text: agg.name, href: `/dashboard/chains/${chainUuid}` },
      ]}
      header={
        <Header
          variant="h1"
          description={`Slug: ${agg.slug} · ${agg.location_count} location${
            agg.location_count === 1 ? '' : 's'
          } · ${agg.scorecard_count} scorecard${agg.scorecard_count === 1 ? '' : 's'}`}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => aggQuery.refetch()}>Refresh</Button>
              <Button iconName="edit" onClick={() => setEditOpen(true)}>
                Edit chain
              </Button>
              <Button
                iconName="add-plus"
                onClick={() =>
                  // Add locations re-uses the full scorecard chain-search flow,
                  // pre-targeted at this chain: discovery runs a real search,
                  // already-added locations are excluded server-side, and the
                  // user picks locations on the familiar search details view.
                  navigate({
                    to: '/dashboard/scorecard/create',
                    search: { tab: 'chain', chain: chainUuid },
                  })
                }
              >
                Add locations
              </Button>
              <Button
                variant="primary"
                iconName="add-plus"
                onClick={() => setCreateOpen(true)}
              >
                Create chain scorecard
              </Button>
            </SpaceBetween>
          }
        >
          {agg.name}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Container header={<Header variant="h2">Aggregate stats</Header>}>
          <ColumnLayout columns={4} variant="text-grid">
            <KeyValuePairs
              items={[{ label: 'Locations', value: String(agg.location_count) }]}
            />
            <KeyValuePairs
              items={[
                {
                  label: 'Avg Google rating',
                  value:
                    agg.avg_google_rating !== null && agg.avg_google_rating !== undefined
                      ? `${agg.avg_google_rating.toFixed(2)} ★`
                      : '—',
                },
              ]}
            />
            <KeyValuePairs
              items={[
                {
                  label: 'Total Google reviews',
                  value: agg.total_google_review_count.toLocaleString(),
                },
              ]}
            />
            <KeyValuePairs
              items={[
                {
                  label: 'Avg reviews / location',
                  value:
                    agg.avg_google_review_count !== null &&
                    agg.avg_google_review_count !== undefined
                      ? agg.avg_google_review_count.toLocaleString()
                      : '—',
                },
              ]}
            />
            <KeyValuePairs
              items={[
                {
                  label: 'Avg Yelp rating',
                  value:
                    agg.avg_yelp_rating !== null && agg.avg_yelp_rating !== undefined
                      ? `${agg.avg_yelp_rating.toFixed(2)} ★`
                      : '—',
                },
              ]}
            />
            <KeyValuePairs
              items={[
                {
                  label: 'Total Yelp reviews',
                  value: agg.total_yelp_review_count.toLocaleString(),
                },
              ]}
            />
            <KeyValuePairs
              items={[{ label: 'Scorecards', value: String(agg.scorecard_count) }]}
            />
            <KeyValuePairs
              items={[{ label: 'Created', value: new Date(agg.created_at).toLocaleString() }]}
            />
          </ColumnLayout>
        </Container>

        <Tabs
          activeTabId={activeTab}
          onChange={({ detail }) => setActiveTab(detail.activeTabId)}
          tabs={[
            {
              id: 'chain-scorecards',
              label: 'Chain scorecards',
              content: <ChainReportsTab chainUuid={chainUuid} />,
            },
            {
              id: 'locations',
              label: `Locations (${agg.location_count})`,
              content: <ChainLocationsTab chainUuid={chainUuid} />,
            },
            {
              id: 'suggested',
              label: 'Suggested links',
              content: (
                <SuggestedLinksTab
                  chainUuid={chainUuid}
                  onLinked={(linkedCount) => {
                    if (linkedCount > 0) {
                      setActiveTab('chain-scorecards');
                      setCreateOpen(true);
                    }
                  }}
                />
              ),
            },
            {
              id: 'searches',
              label: 'Searches',
              content: <ChainSearchesTab chainUuid={chainUuid} />,
            },
            {
              id: 'history',
              label: 'History',
              content: <ChainLocationAuditTab chainUuid={chainUuid} />,
            },
          ]}
        />
      </SpaceBetween>
      <CreateChainScorecardModal
        visible={createOpen}
        onDismiss={() => setCreateOpen(false)}
        chainUuid={chainUuid}
        onCreated={(uuid) => {
          setCreateOpen(false);
          qc.invalidateQueries({ queryKey: ['chain-reports', chainUuid] });
          // Navigate to the public chain scorecard URL. UUID first since
          // the freshly created report's slug isn't echoed back from the
          // create endpoint — the public route accepts either form and
          // /chain/$uuid redirects to /chain/$slug client-side once the
          // first read returns the slug.
          navigate({
            to: '/chain/$slugOrUuid',
            params: { slugOrUuid: uuid },
          });
        }}
      />
      <EditChainModal
        visible={editOpen}
        onDismiss={() => setEditOpen(false)}
        chainUuid={chainUuid}
        currentName={agg.name}
        currentSlug={agg.slug}
        onSaved={() => {
          setEditOpen(false);
          qc.invalidateQueries({ queryKey: ['chain-aggregate', chainUuid] });
          qc.invalidateQueries({ queryKey: ['chains'] });
        }}
      />
    </DashboardViewLayout>
  );
}

// ===========================================================================
// Locations tab — the real PlaceTable, scoped to this chain, with
// chain-scoped filters (business type / industry / source) and
// selection-based removal (unlink) that never re-runs discovery.
// ===========================================================================

const ANY_BUSINESS_TYPE: SelectProps.Option = { label: 'Any business type', value: '' };
const ANY_INDUSTRY: SelectProps.Option = { label: 'Any industry', value: '' };
const ANY_SOURCE: SelectProps.Option = { label: 'Any source', value: '' };

const SOURCE_OPTIONS: SelectProps.Option[] = [
  ANY_SOURCE,
  { label: 'Google', value: 'google' },
  { label: 'Yelp', value: 'yelp' },
  { label: 'BBB', value: 'bbb' },
  { label: 'DealerRater', value: 'dealerrater' },
  { label: 'OpenTable', value: 'opentable' },
  { label: 'TripAdvisor', value: 'tripadvisor' },
  { label: 'Cars.com', value: 'carscom' },
  { label: 'Carfax', value: 'carfax' },
  { label: 'Edmunds', value: 'edmunds' },
  { label: 'CarGurus', value: 'cargurus' },
  { label: 'Booking', value: 'booking' },
  { label: 'Expedia', value: 'expedia' },
  { label: 'GolfPass', value: 'golfpass' },
];

function ChainLocationsTab({ chainUuid }: { chainUuid: string }) {
  const qc = useQueryClient();
  // PlaceTable owns pagination/filter/preferences internally; it just needs
  // controlled now/query/textQuery state. We render the exact same dashboard
  // table (cells, chrome, status) pointed at the chain-scoped places endpoint,
  // so a location row here is identical to a row on /dashboard/places.
  const [now, setNow] = useState(new Date());
  const [query, setQuery] = useState<PropertyFilterQuery>({ tokens: [], operation: 'and' });
  const [textQuery, setTextQuery] = useState('');
  const [businessType, setBusinessType] = useState<SelectProps.Option>(ANY_BUSINESS_TYPE);
  const [industry, setIndustry] = useState<SelectProps.Option>(ANY_INDUSTRY);
  const [source, setSource] = useState<SelectProps.Option>(ANY_SOURCE);
  const [selected, setSelected] = useState<Place[]>([]);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeReason, setRemoveReason] = useState('');
  const [removeError, setRemoveError] = useState<string | null>(null);

  // Industry options come from the same distinct place_type source the main
  // Places table uses (raw PlaceType names as values, prettified labels).
  const metaQuery = useQuery({
    queryKey: ['places', 'meta'],
    queryFn: getPlaceMeta,
    staleTime: 360_000,
  });
  const industryOptions: SelectProps.Option[] = useMemo(() => {
    const opts = ((metaQuery.data?.filteringOptions ?? []) as PropertyFilterOption[])
      .filter((o) => o.propertyKey === 'place_type')
      .map((o) => ({ label: o.label ?? o.value, value: o.value }));
    return [ANY_INDUSTRY, ...opts];
  }, [metaQuery.data]);

  const businessTypeOptions: SelectProps.Option[] = useMemo(
    () => [ANY_BUSINESS_TYPE, ...BUSINESS_TYPES.filter((o) => o.value !== 'Other')],
    [],
  );

  // The filters live in the query-cache key via endpointName so changing a
  // select refetches; the key= remount also resets PlaceTable's internal page.
  const filterKey = `${businessType.value ?? ''}|${industry.value ?? ''}|${source.value ?? ''}`;

  const removeMut = useMutation({
    mutationFn: () =>
      unlinkChainPlaces(
        chainUuid,
        selected.map((p) => (p as ChainPlace).id),
      ),
    onSuccess: async () => {
      setSelected([]);
      setRemoveOpen(false);
      setRemoveReason('');
      setRemoveError(null);
      await Promise.all([
        qc.invalidateQueries({
          predicate: (query) =>
            String(query.queryKey[0] ?? '').startsWith(`chain-places-${chainUuid}`),
        }),
        qc.invalidateQueries({ queryKey: ['chain-aggregate', chainUuid] }),
        qc.invalidateQueries({ queryKey: ['chain-audit', chainUuid] }),
        qc.invalidateQueries({ queryKey: ['chain-suggested', chainUuid] }),
        qc.invalidateQueries({ queryKey: ['chains'] }),
      ]);
      setNow(new Date());
    },
    onError: (e) => setRemoveError(String(e)),
  });

  return (
    <SpaceBetween size="m">
      <PlaceTable
        key={filterKey}
        variant="embedded"
        endpointName={`chain-places-${chainUuid}-${filterKey}`}
        resourceName="Locations"
        description="One row per location, showing its latest scorecard. Only the latest scorecard per location feeds the chain scorecard. Select rows to remove wrongly linked locations — removal never re-runs the search."
        hideAdditionalFilters
        hideExport
        now={now}
        setNow={setNow}
        query={query}
        setQuery={setQuery}
        textQuery={textQuery}
        setTextQuery={setTextQuery}
        selectedItems={selected}
        setSelectedItems={setSelected}
        additionalActions={
          <Button
            disabled={selected.length === 0}
            onClick={() => {
              setRemoveError(null);
              setRemoveOpen(true);
            }}
          >
            Remove from chain ({selected.length})
          </Button>
        }
        extraFilters={
          <>
            <Select
              selectedOption={businessType}
              options={businessTypeOptions}
              onChange={({ detail }) => setBusinessType(detail.selectedOption)}
              ariaLabel="Filter locations by business type"
            />
            <Select
              selectedOption={industry}
              options={industryOptions}
              statusType={metaQuery.isLoading ? 'loading' : 'finished'}
              filteringType="auto"
              onChange={({ detail }) => setIndustry(detail.selectedOption)}
              ariaLabel="Filter locations by industry"
            />
            <Select
              selectedOption={source}
              options={SOURCE_OPTIONS}
              onChange={({ detail }) => setSource(detail.selectedOption)}
              ariaLabel="Filter locations by review source"
            />
          </>
        }
        // Chain-scoped data source. Text search plus the chain-scoped
        // business type / industry / source filters; query/range/sort args
        // from the property filter UI are unused in this scoped view.
        fetchData={(_query, page, limit, _range, _sf, _sd, _now, tq) =>
          listChainPlaces(chainUuid, page, limit, tq || undefined, {
            business_type: businessType.value || undefined,
            industry: industry.value || undefined,
            source: source.value || undefined,
          })
        }
      />
      <Modal
        visible={removeOpen}
        onDismiss={() => setRemoveOpen(false)}
        header={`Remove ${selected.length} location${selected.length === 1 ? '' : 's'} from chain`}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                onClick={() => setRemoveOpen(false)}
                disabled={removeMut.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                loading={removeMut.isPending}
                onClick={() => removeMut.mutate()}
              >
                Remove
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          {removeError && <Alert type="error">{removeError}</Alert>}
          <Alert type="warning" header="Removal changes current chain membership only">
            The Places stay in the system (their reviews and scorecards are kept) — they are
            just unlinked from this chain. Existing public chain scorecards stay point-in-time
            snapshots; create a refreshed chain scorecard afterwards to reflect the change
            publicly. This action is recorded in the History tab.
          </Alert>
          <Box>
            {selected.map((p) => (
              <div key={p.uuid}>
                <b>{p.name}</b> — {p.address}
              </div>
            ))}
          </Box>
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
}

// ===========================================================================
// Suggested links tab — backfill UI
// ===========================================================================

export function SuggestedLinksTab({
  chainUuid,
  onLinked,
}: {
  chainUuid: string;
  onLinked: (linkedCount: number) => void;
}) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<ChainSuggestedLink[]>([]);
  const [filter, setFilter] = useState('');

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['chain-suggested', chainUuid],
    queryFn: () => listChainSuggestedLinks(chainUuid),
  });

  const linkMut = useMutation({
    mutationFn: (place_ids: number[]) => linkChainPlaces(chainUuid, place_ids),
    onSuccess: async (result) => {
      setSelected([]);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['chain-suggested', chainUuid] }),
        qc.invalidateQueries({ queryKey: ['chain-locations', chainUuid] }),
        qc.invalidateQueries({ queryKey: ['chain-reports', chainUuid] }),
        qc.invalidateQueries({ queryKey: ['chain-aggregate', chainUuid] }),
        qc.invalidateQueries({ queryKey: ['chains'] }),
      ]);
      onLinked(result?.linked_count ?? 0);
    },
  });

  const items: ChainSuggestedLink[] = data ?? [];
  const filtered = useMemo(() => {
    if (!filter.trim()) return items;
    const q = filter.toLowerCase();
    return items.filter(
      (s) => s.name.toLowerCase().includes(q) || (s.address ?? '').toLowerCase().includes(q),
    );
  }, [items, filter]);

  return (
    <SpaceBetween size="m">
      <Alert type="info" header="Linking changes current chain membership">
        Linked locations are added to the chain now. Existing public chain scorecards stay
        point-in-time snapshots; create a refreshed chain scorecard after linking to include
        the new locations publicly.
      </Alert>
      <Table
        variant="embedded"
        loading={isLoading}
        items={filtered}
        selectionType="multi"
        selectedItems={selected}
        onSelectionChange={({ detail }) => setSelected(detail.selectedItems)}
        empty={
          <Box textAlign="center" color="inherit">
            <b>No suggestions</b>
            <Box variant="p" color="inherit">
              No unlinked Places match this chain's name. Exact matches are
              auto-linked when a Place is collected — only ambiguous matches
              (e.g. "Jiffy Lube - Westheimer") show up here for confirmation.
            </Box>
          </Box>
        }
        filter={
          <TextFilter
            filteringText={filter}
            filteringPlaceholder="Filter by name or address"
            onChange={({ detail }) => setFilter(detail.filteringText)}
            countText={`${filtered.length} match${filtered.length === 1 ? '' : 'es'}`}
          />
        }
        header={
          <Header
            variant="h2"
            counter={`(${items.length})`}
            description="Places not yet linked to this chain whose name looks like a match. Review carefully — false positives like 'Subway Station Diner' will appear and should NOT be linked."
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={() => refetch()} loading={isFetching} iconName="refresh" />
                <Button
                  variant="primary"
                  disabled={selected.length === 0 || linkMut.isPending}
                  loading={linkMut.isPending}
                  onClick={() => linkMut.mutate(selected.map((s) => s.place_id))}
                >
                  Link to chain ({selected.length})
                </Button>
              </SpaceBetween>
            }
          >
            Suggested links
          </Header>
        }
        columnDefinitions={[
          {
            id: 'name',
            header: 'Name',
            cell: (s) => s.name,
            minWidth: 220,
          },
          {
            id: 'match',
            header: 'Match',
            cell: (s) => {
              // exact: name == chain (very safe)
              // delimiter_prefix: "Chain - Location" pattern (also safe)
              // prefix: bare prefix match (review carefully — false positives like
              // "Subway Station Diner" matching "subway" land here)
              const type =
                s.match_kind === 'exact' || s.match_kind === 'delimiter_prefix'
                  ? 'success'
                  : 'warning';
              const label =
                s.match_kind === 'delimiter_prefix' ? 'prefix • dash' : s.match_kind;
              return <StatusIndicator type={type}>{label}</StatusIndicator>;
            },
            width: 140,
          },
          {
            id: 'address',
            header: 'Address',
            cell: (s) => s.address ?? '—',
            minWidth: 300,
          },
          {
            id: 'rating',
            header: 'Rating',
            cell: (s) =>
              s.rating !== null && s.rating !== undefined
                ? `${s.rating.toFixed(1)} ★ (${s.total_review_count ?? '?'})`
                : '—',
            width: 130,
          },
          {
            id: 'place_id',
            header: 'Google place_id',
            cell: (s) => <Box variant="code">{s.google_place_id}</Box>,
            width: 280,
          },
        ]}
      />
    </SpaceBetween>
  );
}

// ===========================================================================
// Searches tab — this chain's discovery searches (replaces the removed
// standalone Chain searches page)
// ===========================================================================

export function ChainSearchesTab({ chainUuid }: { chainUuid: string }) {
  const navigate = useNavigate();
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['chain-searches', 'by-chain', chainUuid],
    queryFn: () => listChainSearches(1, 200),
    refetchInterval: (q) => {
      const mine = (q.state.data?.items ?? []).filter((i) => i.chain_uuid === chainUuid);
      const active = mine.some(
        (i) => !['complete', 'failed', 'cancelled'].includes(i.status),
      );
      return active ? 5000 : false;
    },
  });

  const items = useMemo(
    () => (data?.items ?? []).filter((i) => i.chain_uuid === chainUuid),
    [data, chainUuid],
  );

  return (
    <Table
      variant="embedded"
      loading={isLoading}
      items={items}
      empty={
        <Box textAlign="center" color="inherit">
          <b>No searches for this chain</b>
          <Box variant="p" color="inherit">
            Discovery searches that created or extended this chain will show up
            here. Start one with "New chain search" on the Chains page.
          </Box>
        </Box>
      }
      header={
        <Header
          variant="h2"
          counter={`(${items.length})`}
          description="Discovery jobs that fed this chain. Open one to review its candidate locations or re-run selection."
          actions={
            <Button iconName="refresh" loading={isFetching} onClick={() => refetch()} />
          }
        >
          Searches
        </Header>
      }
      columnDefinitions={[
        {
          id: 'search',
          header: 'Search',
          cell: (i) => (
            <Link
              href={`/dashboard/chains/searches/${i.uuid}`}
              onFollow={(e) => {
                e.preventDefault();
                navigate({
                  to: '/dashboard/chains/searches/$uuid',
                  params: { uuid: i.uuid },
                });
              }}
            >
              {i.chain_name}
            </Link>
          ),
          minWidth: 200,
        },
        {
          id: 'region',
          header: 'Region',
          cell: (i) => `${i.region_type}: ${i.region_value || '—'}`,
          width: 180,
        },
        {
          id: 'status',
          header: 'Status',
          cell: (i) => (
            <StatusIndicator type={SEARCH_STATUS_INDICATOR[i.status] ?? 'in-progress'}>
              {i.status}
            </StatusIndicator>
          ),
          width: 140,
        },
        {
          id: 'progress',
          header: 'Progress',
          cell: (i) =>
            `${i.grid_points_searched}/${i.grid_points_total} pts · ` +
            `${i.locations_found} found · ` +
            `${i.scorecards_complete}/${i.scorecards_created} scorecards done`,
          minWidth: 280,
        },
        {
          id: 'created',
          header: 'Created',
          cell: (i) => new Date(i.created_at).toLocaleString(),
          width: 190,
        },
      ]}
    />
  );
}

// ===========================================================================
// History tab — chain location membership audit trail
// ===========================================================================

export function ChainLocationAuditTab({ chainUuid }: { chainUuid: string }) {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['chain-audit', chainUuid],
    queryFn: () => listChainLocationAudit(chainUuid, 200),
  });
  const items = data ?? [];

  return (
    <Table
      variant="embedded"
      loading={isLoading}
      items={items}
      empty={
        <Box textAlign="center" color="inherit">
          <b>No membership changes yet</b>
          <Box variant="p" color="inherit">
            Manual adds, links, moves, and removals of this chain's locations
            are recorded here.
          </Box>
        </Box>
      }
      header={
        <Header
          variant="h2"
          counter={`(${items.length})`}
          description="Audit trail of location membership changes — who was added, linked, moved, or removed, and why."
          actions={
            <Button iconName="refresh" loading={isFetching} onClick={() => refetch()} />
          }
        >
          Location history
        </Header>
      }
      columnDefinitions={[
        {
          id: 'when',
          header: 'When',
          cell: (a) => new Date(a.created_at + 'Z').toLocaleString(),
          width: 200,
        },
        {
          id: 'action',
          header: 'Action',
          cell: (a) => AUDIT_ACTION_LABEL[a.action] ?? a.action,
          width: 200,
        },
        {
          id: 'place',
          header: 'Place id',
          cell: (a) => (a.place_id != null ? <Box variant="code">{a.place_id}</Box> : '—'),
          width: 120,
        },
        {
          id: 'reason',
          header: 'Reason',
          cell: (a) => a.reason ?? '—',
          minWidth: 260,
        },
      ]}
    />
  );
}

// ===========================================================================
// Edit chain modal — rename / slug override, no location changes
// ===========================================================================

function EditChainModal({
  visible,
  onDismiss,
  chainUuid,
  currentName,
  currentSlug,
  onSaved,
}: {
  visible: boolean;
  onDismiss: () => void;
  chainUuid: string;
  currentName: string;
  currentSlug: string;
  onSaved: () => void;
}) {
  const [name, setName] = useState(currentName);
  const [slug, setSlug] = useState(currentSlug);
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: () =>
      updateChain(chainUuid, {
        name: name.trim() !== currentName ? name.trim() : undefined,
        slug: slug.trim() !== currentSlug ? slug.trim() : undefined,
      }),
    onSuccess: () => onSaved(),
    onError: (e) => setError(String(e)),
  });

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Edit chain"
      size="medium"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss} disabled={saveMut.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={saveMut.isPending}
              disabled={!name.trim()}
              onClick={() => saveMut.mutate()}
            >
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error && <Alert type="error">{error}</Alert>}
        <Alert type="info">
          Renaming fixes the display name/slug without touching locations or re-running
          any search. Existing public scorecard URLs using the old slug keep working.
        </Alert>
        <FormField label="Name">
          <Input value={name} onChange={({ detail }) => setName(detail.value)} />
        </FormField>
        <FormField
          label="Slug"
          description="URL-safe identifier. Leave as-is to derive from the name."
        >
          <Input value={slug} onChange={({ detail }) => setSlug(detail.value)} />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}

// ===========================================================================
// Chain scorecards tab — list of past chain scorecard snapshots for this chain
// ===========================================================================

function ChainReportsTab({ chainUuid }: { chainUuid: string }) {
  const navigate = useNavigate();
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['chain-reports', chainUuid],
    queryFn: () => listChainReports(chainUuid, 1, 100),
    refetchInterval: 10000,
  });
  const items: ChainReport[] = data?.items ?? [];

  return (
    <Table
      variant="embedded"
      loading={isLoading}
      items={items}
      empty={
        <Box textAlign="center" color="inherit">
          <b>No chain scorecards yet</b>
          <Box variant="p" color="inherit">
            Click "Create chain scorecard" above to take a snapshot of this
            chain's aggregate stats across all linked locations.
          </Box>
        </Box>
      }
      header={
        <Header
          variant="h2"
          counter={`(${items.length})`}
          actions={<Button iconName="refresh" loading={isFetching} onClick={() => refetch()} />}
          description="Point-in-time snapshots of chain-wide stats. Each row links to the full dashboard view + public sharing link."
        >
          Chain scorecards
        </Header>
      }
      columnDefinitions={[
        {
          id: 'created',
          header: 'Created',
          // Link to the PUBLIC chain scorecard at /chain/$slug — same URL
          // anyone receives in an email. No separate internal view exists
          // (chain scorecards are public-by-knowledge-of-the-slug, same as
          // per-place scorecards).
          cell: (r) => {
            const target = r.slug ?? r.uuid;
            return (
              <Link
                href={`/chain/${target}`}
                onFollow={(e) => {
                  e.preventDefault();
                  navigate({
                    to: '/chain/$slugOrUuid',
                    params: { slugOrUuid: target },
                  });
                }}
              >
                {new Date(r.created_at + 'Z').toLocaleString()}
              </Link>
            );
          },
          minWidth: 220,
        },
        {
          id: 'business_type',
          header: 'Business type',
          cell: (r) => r.business_type ?? '—',
          width: 160,
        },
        {
          id: 'region',
          header: 'Region',
          cell: (r) =>
            r.region_type && r.region_value
              ? `${r.region_type}: ${r.region_value}`
              : 'all linked',
          width: 160,
        },
        {
          id: 'periods',
          header: 'Periods',
          cell: (r) => `${r.periods.length} period${r.periods.length === 1 ? '' : 's'}`,
          width: 120,
        },
        {
          id: 'locations',
          header: 'Chain size',
          cell: (r) => `${r.location_count} locations`,
          width: 130,
        },
        {
          id: 'hidden',
          header: '',
          cell: (r) => (r.hidden ? <StatusIndicator type="stopped">hidden</StatusIndicator> : null),
          width: 90,
        },
      ]}
    />
  );
}

// ===========================================================================
// Create chain scorecard modal
// ===========================================================================

function CreateChainScorecardModal({
  visible,
  onDismiss,
  chainUuid,
  onCreated,
}: {
  visible: boolean;
  onDismiss: () => void;
  chainUuid: string;
  onCreated: (uuid: string) => void;
}) {
  // business_type + sources + ranges are all auto-determined server-side
  // — place scorecards don't offer range selection either, so chain
  // shouldn't. Default ranges are 30/60/90/180 (see DEFAULT_RANGES in
  // services/chain_report.py).
  const [regionType, setRegionType] = useState<SelectProps.Option>({
    label: 'All linked locations',
    value: '',
  });
  const [regionValue, setRegionValue] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () =>
      createChainReport(chainUuid, {
        // No `ranges` — let the backend default kick in (all four).
        region_type: regionType.value || undefined,
        region_value: regionType.value ? regionValue.trim() || undefined : undefined,
        notes: notes.trim() || undefined,
      }),
    onSuccess: (data) => {
      if (data?.uuid) {
        onCreated(data.uuid);
      } else {
        setError('Created but no UUID returned');
      }
    },
    onError: (e) => setError(String(e)),
  });

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Create chain scorecard"
      size="medium"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss} disabled={createMut.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => createMut.mutate()}
              loading={createMut.isPending}
            >
              Create scorecard
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error && <Alert type="error">{error}</Alert>}
        <Alert type="info">
          Snapshots the chain's current linked locations into a single scorecard. Linked
          location changes do not update older public scorecards; create this refreshed
          scorecard when current membership should be reflected publicly. All values are{' '}
          <b>simple averages</b> across the chain's linked Places at this moment.
        </Alert>
        <FormField label="Region scope" description="Limit to a subset of linked locations (optional).">
          <SpaceBetween direction="horizontal" size="xs">
            <Select
              selectedOption={regionType}
              options={[
                { label: 'All linked locations', value: '' },
                { label: 'State', value: 'state' },
                { label: 'ZIP', value: 'zip' },
                { label: 'City', value: 'city' },
              ]}
              onChange={({ detail }) => setRegionType(detail.selectedOption)}
            />
            {regionType.value && (
              <Input
                value={regionValue}
                placeholder={regionType.value === 'state' ? 'TX' : regionType.value === 'zip' ? '75201' : 'Houston'}
                onChange={({ detail }) => setRegionValue(detail.value)}
              />
            )}
          </SpaceBetween>
        </FormField>
        <FormField label="Notes (optional)">
          <Input value={notes} onChange={({ detail }) => setNotes(detail.value)} />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}
